import { useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/feature/TopBar";
import Footer from "@/components/feature/Footer";
import LpForm from "../components/LpForm";
import { PHONE } from "@/mocks/updiag";
import { SHOW_REVIEWS } from "@/config/features";

const faqItems = [
  { q: "Qu'est-ce qui est inclus dans le Pack Vente ?", a: "Le Pack Vente (DDT complet) inclut : DPE, Diagnostic Amiante (si avant 1997), CREP Plomb (si avant 1949), Diagnostic Électricité (si +15 ans), Diagnostic Gaz (si +15 ans), Mesurage Loi Carrez (si copropriété). Tout est réalisé en un seul rendez-vous." },
  { q: "Combien de temps dure la visite pour un pack complet ?", a: "La visite dure entre 2h et 3h pour un appartement standard, 3h à 4h pour une maison. Vous recevez tous vos rapports certifiés par email le jour même." },
  { q: "Les rapports sont-ils acceptés par les notaires ?", a: "Oui, tous nos rapports sont certifiés COFRAC et acceptés par l'ensemble des notaires et agences immobilières en France." },
  { q: "Quelle est la différence entre Pack Vente et Pack Location ?", a: "Le Pack Vente (DDT) est obligatoire pour la vente et inclut plus de diagnostics. Le Pack Location est adapté aux bailleurs et inclut DPE, Plomb, Électricité, Gaz. Les deux sont réalisés en un seul rendez-vous." },
  { q: "Puis-je commander un pack pour plusieurs biens ?", a: "Oui, nous proposons des tarifs dégressifs pour les propriétaires de plusieurs biens ou les agences immobilières. Contactez-nous pour un devis personnalisé." },
];

function FaqItemLP({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`bg-white rounded-2xl border transition-all overflow-hidden ${open ? "border-[#F97316]" : "border-gray-100"}`}>
      <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpen(!open)}>
        <span className={`font-semibold text-sm pr-4 ${open ? "text-[#F97316]" : "text-[#0F3B5F]"}`}>{q}</span>
        <i className={`ri-arrow-down-s-line text-xl flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-[#F97316]" : "text-gray-400"}`}></i>
      </button>
      {open && <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-50 pt-4">{a}</div>}
    </div>
  );
}

export default function LpPackDiagnostic() {
  const [activeTab, setActiveTab] = useState<"vente" | "location">("vente");

  const packs = {
    vente: {
      name: "Pack Vente",
      subtitle: "Dossier de Diagnostic Technique (DDT) complet",
      price: "Dès 280 €",
      badge: "Vente immobilière",
      color: "border-[#0F3B5F]",
      items: [
        { label: "DPE", desc: "Diagnostic de Performance Énergétique", included: true },
        { label: "Amiante", desc: "Obligatoire si avant juillet 1997", included: true },
        { label: "Plomb (CREP)", desc: "Obligatoire si avant 1949", included: true },
        { label: "Électricité", desc: "Obligatoire si installation +15 ans", included: true },
        { label: "Gaz", desc: "Obligatoire si installation +15 ans", included: true },
        { label: "Loi Carrez", desc: "Obligatoire en copropriété", included: true },
        { label: "ERP", desc: "État des Risques et Pollutions", included: true },
      ],
    },
    location: {
      name: "Pack Location",
      subtitle: "Diagnostics obligatoires pour louer",
      price: "Dès 180 €",
      badge: "Location immobilière",
      color: "border-[#F97316]",
      items: [
        { label: "DPE", desc: "Diagnostic de Performance Énergétique", included: true },
        { label: "Plomb (CREP)", desc: "Obligatoire si avant 1949", included: true },
        { label: "Électricité", desc: "Obligatoire si installation +15 ans", included: true },
        { label: "Gaz", desc: "Obligatoire si installation +15 ans", included: true },
        { label: "ERP", desc: "État des Risques et Pollutions", included: true },
        { label: "Amiante", desc: "Non obligatoire à annexer au bail", included: false },
        { label: "Loi Carrez", desc: "Non applicable pour la location", included: false },
      ],
    },
  };

  const activePack = packs[activeTab];

  return (
    <>
      <title>Pack Diagnostic Vente & Location Paris IDF — DDT Complet Dès 180 € | Up Diag</title>
      <meta name="description" content="Pack diagnostic immobilier vente et location à Paris et en IDF. DDT complet dès 280 €, Pack Location dès 180 €. Certifié COFRAC, un seul RDV, rapport le jour même." />
      <meta name="robots" content="noindex" />
      <TopBar />

      {/* LP Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#0F3B5F] rounded-xl flex items-center justify-center">
              <i className="ri-home-gear-fill text-white text-lg"></i>
            </div>
            <div>
              <div className="font-bold text-lg text-[#0F3B5F] leading-none" style={{ fontFamily: "Barlow, sans-serif" }}>Up Diag</div>
              <div className="text-[9px] text-gray-400 leading-none">Diagnostic immobilier certifié</div>
            </div>
          </Link>
          <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center gap-2 px-5 py-2.5 bg-[#F97316] text-white rounded-xl font-bold text-sm hover:bg-orange-600 transition-all whitespace-nowrap">
            <i className="ri-phone-fill"></i> {PHONE}
          </a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative min-h-[600px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/img/readdy/lp3-hero.jpg"
              alt="Pack diagnostic vente location"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F3B5F]/92 via-[#0F3B5F]/75 to-[#0F3B5F]/30"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6 py-16 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#F97316] text-white text-xs font-bold px-4 py-2 rounded-full mb-6">
                  <i className="ri-stack-fill"></i>
                  Guichet unique — Tous diagnostics en 1 RDV
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5" style={{ fontFamily: "Barlow, sans-serif" }}>
                  Pack Diagnostic<br />
                  <span className="text-[#F97316]">Vente &amp; Location<br />Paris &amp; IDF</span>
                </h1>
                <p className="text-lg text-white/85 mb-8 max-w-lg">
                  Tous vos diagnostics obligatoires réunis en un seul rendez-vous. DDT complet dès 280 €, Pack Location dès 180 €. Certifié COFRAC, rapport le jour même.
                </p>
                <div className="flex flex-col gap-3 mb-8">
                  {[
                    { icon: "ri-stack-fill", text: "Tous diagnostics en 1 seul rendez-vous", color: "text-[#F97316]" },
                    { icon: "ri-award-fill", text: "Certifié COFRAC — Accepté par tous les notaires", color: "text-[#16A34A]" },
                    { icon: "ri-timer-flash-fill", text: "Intervention sous 48h dans toute l'IDF", color: "text-[#F59E0B]" },
                    { icon: "ri-file-check-line", text: "Tous les rapports le jour même par email", color: "text-white" },
                  ].map((pt) => (
                    <div key={pt.text} className="flex items-center gap-3">
                      <i className={`${pt.icon} ${pt.color} text-lg flex-shrink-0`}></i>
                      <span className="text-white/90 text-sm">{pt.text}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4">
                  <div className="bg-white/10 border border-white/20 rounded-xl px-5 py-3 text-center">
                    <div className="text-2xl font-extrabold text-white" style={{ fontFamily: "Barlow, sans-serif" }}>Dès 280 €</div>
                    <div className="text-xs text-white/60">Pack Vente</div>
                  </div>
                  <div className="bg-[#F97316]/20 border border-[#F97316]/40 rounded-xl px-5 py-3 text-center">
                    <div className="text-2xl font-extrabold text-white" style={{ fontFamily: "Barlow, sans-serif" }}>Dès 180 €</div>
                    <div className="text-xs text-white/60">Pack Location</div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center lg:justify-end">
                <div className="w-full max-w-md">
                  <LpForm
                    preselectedDiag="pack-vente"
                    formTitle="Devis pack gratuit"
                    formSubtitle="Vente ou location — Réponse sous 2h"
                    ctaLabel="Obtenir mon devis pack gratuit"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="bg-white border-b border-gray-100 py-8">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex flex-wrap items-center justify-center gap-8">
              {[
                { icon: "ri-award-fill", label: "Certifié COFRAC", color: "text-[#F97316]" },
                { icon: "ri-stack-fill", label: "1 seul rendez-vous", color: "text-[#0F3B5F]" },
                { icon: "ri-timer-flash-fill", label: "RDV sous 48h", color: "text-[#F97316]" },
                { icon: "ri-file-check-line", label: "Rapports le jour même", color: "text-[#0F3B5F]" },
                ...(SHOW_REVIEWS ? [{ icon: "ri-star-fill", label: "4.9/5 Google", color: "text-[#F59E0B]" }] : []),
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-sm font-semibold text-gray-600">
                  <i className={`${item.icon} ${item.color} text-lg`}></i>
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparatif packs */}
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>
                Choisissez votre pack
              </h2>
              <p className="text-gray-500">Vente ou location, nous avons le pack adapté à votre situation</p>
            </div>

            {/* Toggle */}
            <div className="flex justify-center mb-8">
              <div className="bg-white border border-gray-200 rounded-full p-1 flex gap-1">
                <button
                  onClick={() => setActiveTab("vente")}
                  className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${activeTab === "vente" ? "bg-[#0F3B5F] text-white" : "text-gray-500 hover:text-[#0F3B5F]"}`}
                >
                  Pack Vente
                </button>
                <button
                  onClick={() => setActiveTab("location")}
                  className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${activeTab === "location" ? "bg-[#F97316] text-white" : "text-gray-500 hover:text-[#F97316]"}`}
                >
                  Pack Location
                </button>
              </div>
            </div>

            <div className={`bg-white rounded-2xl border-2 ${activePack.color} p-8 max-w-xl mx-auto`}>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="inline-block bg-orange-100 text-[#F97316] text-xs font-bold px-3 py-1 rounded-full mb-2">{activePack.badge}</span>
                  <h3 className="text-2xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>{activePack.name}</h3>
                  <p className="text-sm text-gray-400">{activePack.subtitle}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>{activePack.price}</div>
                  <div className="text-xs text-gray-400">TTC, déplacement inclus</div>
                </div>
              </div>
              <div className="flex flex-col gap-3 mb-7">
                {activePack.items.map((item) => (
                  <div key={item.label} className={`flex items-center gap-3 p-3 rounded-xl ${item.included ? "bg-green-50" : "bg-gray-50 opacity-50"}`}>
                    <i className={`${item.included ? "ri-checkbox-circle-fill text-[#16A34A]" : "ri-close-circle-fill text-gray-300"} text-xl flex-shrink-0`}></i>
                    <div>
                      <span className="font-bold text-[#0F3B5F] text-sm">{item.label}</span>
                      <span className="text-xs text-gray-400 ml-2">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="w-full py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Devis {activePack.name} gratuit <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Avantages guichet unique */}
        <section className="bg-white py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>
                Pourquoi choisir notre pack ?
              </h2>
              <p className="text-gray-500">Le guichet unique : tous vos diagnostics en un seul rendez-vous</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: "ri-stack-fill", title: "1 seul rendez-vous", desc: "Tous vos diagnostics réalisés en une seule visite. Gain de temps considérable pour vous et vos acheteurs.", color: "text-[#F97316]", bg: "bg-orange-50" },
                { icon: "ri-price-tag-3-fill", title: "Tarif tout compris", desc: "Un prix fixe pour l'ensemble des diagnostics. Pas de mauvaise surprise, pas de frais cachés.", color: "text-[#0F3B5F]", bg: "bg-blue-50" },
                { icon: "ri-award-fill", title: "Certifié COFRAC", desc: "Tous nos diagnostiqueurs sont certifiés COFRAC. Rapports acceptés par tous les notaires de France.", color: "text-[#16A34A]", bg: "bg-green-50" },
                { icon: "ri-file-check-line", title: "Rapports le jour même", desc: "Tous vos rapports certifiés envoyés par email le jour de l'intervention. Idéal pour respecter vos délais.", color: "text-[#F97316]", bg: "bg-orange-50" },
                { icon: "ri-timer-flash-fill", title: "RDV sous 48h", desc: "Intervention rapide dans toute l'Île-de-France. Urgence ? Contactez-nous pour un RDV le jour même.", color: "text-[#0F3B5F]", bg: "bg-blue-50" },
                { icon: "ri-customer-service-2-fill", title: "Accompagnement", desc: "Notre équipe vous explique les résultats et répond à toutes vos questions. Disponible 6j/7.", color: "text-[#16A34A]", bg: "bg-green-50" },
              ].map((item) => (
                <div key={item.title} className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 hover:-translate-y-1 transition-all duration-300">
                  <div className={`w-12 h-12 flex items-center justify-center ${item.bg} rounded-xl mb-4`}>
                    <i className={`${item.icon} ${item.color} text-2xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#0F3B5F] mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Témoignages */}


        {SHOW_REVIEWS && (
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-extrabold text-[#0F3B5F]" style={{ fontFamily: "Barlow, sans-serif" }}>
                Ils ont choisi notre pack
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: "Thomas B.", loc: "Boulogne-Billancourt (92)", service: "Pack Vente", text: "Pack vente complet en une seule visite. Rapport parfait pour mon notaire. Je recommande !", img: "/img/readdy/lp3t1.jpg" },
                { name: "Julien F.", loc: "Massy (91)", service: "Pack Location", text: "Pack location rapide et efficace. Mon locataire a pu signer le bail dans les délais.", img: "/img/readdy/lp3t2.jpg" },
                { name: "Pierre-Antoine L.", loc: "Versailles (78)", service: "Pack Vente", text: "Intervention à Versailles, ponctuel et professionnel. Tous les rapports reçus le soir même.", img: "/img/readdy/lp3t3.jpg" },
              ].map((t) => (
                <div key={t.name} className="bg-white rounded-2xl p-6 border-l-4 border-[#F97316]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-[#F59E0B] text-sm"></i>)}
                    </div>
                    <span className="text-xs bg-orange-100 text-[#F97316] font-bold px-2 py-0.5 rounded-full">{t.service}</span>
                  </div>
                  <p className="text-gray-600 text-sm italic mb-4">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover object-top" loading="lazy" />
                    <div>
                      <div className="font-bold text-[#0F3B5F] text-sm">{t.name}</div>
                      <div className="text-xs text-gray-400">{t.loc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>


        )}

        {/* FAQ */}
        <section className="bg-white py-20">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-2xl font-extrabold text-[#0F3B5F] mb-8 text-center" style={{ fontFamily: "Barlow, sans-serif" }}>
              Questions fréquentes — Pack Diagnostic
            </h2>
            <div className="flex flex-col gap-3">
              {faqItems.map((item, i) => <FaqItemLP key={i} q={item.q} a={item.a} />)}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="bg-[#0F3B5F] py-16">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              Prêt à obtenir votre pack diagnostic ?
            </h2>
            <p className="text-white/75 mb-8">Devis gratuit — 1 seul RDV — Rapports le jour même</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-file-list-3-line"></i> Obtenir mon devis pack gratuit
              </button>
              <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-bold hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-phone-fill"></i> {PHONE}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
